import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  Gauge, Buildings, HardDrives, Envelope, Funnel, PlugsConnected,
  BellRinging, Flask, SignOut, Television, Pulse, Bell, Users, Plug, ShieldWarning,
  PaperPlaneTilt, BookOpen, UsersThree as UsersIcon, UserCircle,
} from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import Logo from "@/components/Logo";

// Grouped nav — a soft label sits above each section so the sidebar reads
// like a real product menu instead of one long list.
const NAV_GROUPS = [
  { label: "Overview", items: [
    { to: "/app",          label: "Dashboard",        icon: Gauge },
    { to: "/app/tv",       label: "TV / NOC",         icon: Television, accent: true },
    { to: "/app/incidents",label: "Incidents & MTTR", icon: ShieldWarning },
  ]},
  { label: "Monitoring", items: [
    { to: "/app/monitors",           label: "Uptime monitors",    icon: Pulse },
    { to: "/app/jobs",               label: "Backup jobs",        icon: HardDrives },
    { to: "/app/vendor-integrations",label: "Vendor APIs",        icon: Plug },
    { to: "/app/mailboxes",          label: "Inbound mailboxes",  icon: Envelope },
  ]},
  { label: "Customers", items: [
    { to: "/app/clients",      label: "Clients",       icon: Buildings },
    { to: "/app/portal-users", label: "Client portal", icon: Users },
    { to: "/app/users",        label: "Team & users",  icon: UsersIcon, adminOnly: true },
  ]},
  { label: "Reports", items: [
    { to: "/app/reports", label: "Inbox / reports", icon: Envelope },
    { to: "/app/rules",   label: "Parsing rules",   icon: Funnel },
  ]},
  { label: "Alerts", items: [
    { to: "/app/alert-channels", label: "Alert channels",  icon: Bell },
    { to: "/app/digest",         label: "Scheduled digest",icon: PaperPlaneTilt },
    { to: "/app/alerts",         label: "Alert history",   icon: BellRinging },
  ]},
  { label: "Setup", items: [
    { to: "/app/integrations", label: "Integrations", icon: PlugsConnected },
    { to: "/app/test-parser",  label: "Test parser",  icon: Flask },
    { to: "/app/account",      label: "Account & sessions", icon: UserCircle },
    { to: "/app/help",         label: "Tech guide",   icon: BookOpen },
  ]},
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const initials = (user?.name || user?.email || "?")
    .split(/[\s@.]+/).filter(Boolean).slice(0, 2)
    .map(s => s[0].toUpperCase()).join("");

  return (
    <div className="min-h-screen flex bg-background-deep text-foreground">
      <aside className="w-64 shrink-0 border-r border-slate-800/80 bg-brand-navy/40 flex flex-col">
        {/* Logo header — compact and proportional */}
        <div className="h-24 flex items-center justify-center px-5 bg-brand-navy border-b border-slate-800/80"
          data-testid="sidebar-logo-header">
          <Logo size={62} />
        </div>

        {/* Nav — grouped, sans-serif, subtle borders */}
        <nav className="flex-1 py-4 px-3 space-y-5 overflow-y-auto" data-testid="sidebar-nav">
          {NAV_GROUPS.map(group => {
            const items = group.items.filter(i => !i.adminOnly || user?.role === "admin");
            if (!items.length) return null;
            return (
              <div key={group.label}>
                <div className="px-3 mb-1.5 text-[10px] font-semibold tracking-[0.15em] uppercase text-slate-500">
                  {group.label}
                </div>
                <div className="space-y-0.5">
                  {items.map(({ to, label, icon: Icon, accent }) => (
                    <NavLink
                      key={to}
                      to={to}
                      end={to === "/app"}
                      data-testid={`nav-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      className={({ isActive }) =>
                        `group flex items-center gap-2.5 px-3 py-2 rounded-md text-[13.5px] transition-all ${
                          isActive
                            ? "bg-brand-cyan/12 text-white shadow-[inset_2px_0_0_0_#00C7FF]"
                            : "text-slate-300 hover:text-white hover:bg-white/[0.04]"
                        }`
                      }
                    >
                      <Icon size={16}
                        weight={accent ? "duotone" : "regular"}
                        className="shrink-0 text-slate-400 group-hover:text-brand-cyan transition-colors" />
                      <span className="truncate">{label}</span>
                    </NavLink>
                  ))}
                </div>
              </div>
            );
          })}
        </nav>

        {/* User chip — cleaner, sans-serif, avatar bubble */}
        <div className="p-3 border-t border-slate-800/80">
          <div className="flex items-center gap-2.5 px-2.5 py-2.5 rounded-md bg-white/[0.03] hover:bg-white/[0.06] transition-colors">
            <div className="w-8 h-8 rounded-full bg-brand-cyan/15 text-brand-cyan text-xs font-bold flex items-center justify-center shrink-0"
              data-testid="sidebar-user-avatar">
              {initials || "•"}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[13px] text-white font-medium truncate" data-testid="sidebar-user-name">
                {user?.name || "—"}
              </div>
              <div className="text-[11px] text-slate-400 truncate" data-testid="sidebar-user-email">
                {user?.email}
              </div>
            </div>
            <button
              onClick={async () => { await logout(); navigate("/login"); }}
              data-testid="logout-btn"
              className="p-1.5 -mr-1 text-slate-400 hover:text-red-400 rounded transition-colors"
              title="Sign out"
            >
              <SignOut size={15} />
            </button>
          </div>
        </div>
      </aside>

      <main className="flex-1 flex flex-col min-w-0">{children}</main>
    </div>
  );
}

export function PageHeader({ title, subtitle, actions }) {
  return (
    <header className="h-16 px-8 flex items-center justify-between border-b border-slate-800/80 bg-brand-navy/60 backdrop-blur-md sticky top-0 z-10">
      <div className="min-w-0">
        <h1 className="font-display text-lg font-bold tracking-tight truncate text-white leading-tight"
          data-testid="page-title">{title}</h1>
        {subtitle && (
          <p className="text-[13px] text-slate-400 truncate leading-tight mt-0.5">{subtitle}</p>
        )}
      </div>
      <div className="flex items-center gap-2 shrink-0">{actions}</div>
    </header>
  );
}
